import React, { useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { Card } from '../ui/card';
import Badge from '../ui/Badge';
import { useGPS } from '../../contexts/GPSContext';
import GPSCalendarEventModal from './GPSCalendarEventModal';
import GPSCalendarEventChip from './GPSCalendarEventChip';
import { actionTone } from './gpsCalendarTones.js';
import { watchPopover } from './gpsCalendarPopover.js';
import { gpsCalendarEvents, UNKNOWN_ACTION } from '../../utils/gpsCalendarEvents.js';
import { toDateParam } from '../../utils/dateUtils.js';

// Days travel as 'YYYY-MM-DD' strings and months as 'YYYY-MM'; a Date only to print them.
const fmt = (day, opts) => new Date(`${day}T00:00:00Z`).toLocaleDateString('fr-FR', { timeZone: 'UTC', ...opts });
const plural = (n, word) => `${n} ${word}${n > 1 ? 's' : ''}`;

// aria-disabled rather than disabled: a disabled button drops the keyboard focus at the last month.
const NavButton = ({ label, onClick, disabled, children }) => (
  <button
    type="button"
    aria-label={label}
    aria-disabled={disabled}
    onClick={disabled ? undefined : onClick}
    className={`p-1.5 rounded-md text-gray-500 dark:text-gray-400 transition-colors focus:outline-none
                focus-visible:ring-2 focus-visible:ring-[#6e3ff3] dark:focus-visible:ring-[#a78bfa] ${disabled
      ? 'cursor-default'
      : 'hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white'}`}
  >
    {/* Dim the icon, not the button: opacity on the button would fade its focus ring too. */}
    <svg className={`w-4 h-4 ${disabled ? 'opacity-30' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      {children}
    </svg>
  </button>
);

const GPSCalendar = () => {
  const { issues } = useGPS();
  const [selected, setSelected] = useState(null);
  const [openMonth, setOpenMonth] = useState(null);
  const gridRef = useRef(null);
  const today = toDateParam(new Date());
  const events = useMemo(() => gpsCalendarEvents(issues, today), [issues, today]);
  const hasEvents = events.length > 0;

  const { days, months, byType } = useMemo(() => {
    const sorted = events.map((e) => e.start).sort();
    const counts = events.reduce((acc, e) => {
      const type = e.extendedProps.issue?.request_type || UNKNOWN_ACTION;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    return {
      days: sorted,
      // The current month always opens the tab; arrows then skip straight to months with work.
      months: [...new Set([today.slice(0, 7), ...sorted.map((d) => d.slice(0, 7))])].sort(),
      byType: Object.entries(counts).sort((a, b) => b[1] - a[1]),
    };
  }, [events, today]);

  // Held as a month key, not an index: a filter reshapes `months` and an index would drift.
  const index = Math.max(0, months.indexOf(openMonth));
  const month = months[index];

  // The grid only exists once there is an intervention to show.
  useEffect(() => (gridRef.current ? watchPopover(gridRef.current) : undefined), [hasEvents]);

  if (!hasEvents) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-12 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">Aucune intervention à venir sur cette période.</p>
      </div>
    );
  }

  const first = days[0];
  const last = days[days.length - 1];
  const withYear = first.slice(0, 4) !== last.slice(0, 4) || first.slice(0, 4) !== today.slice(0, 4);
  const dayLabel = (day) => fmt(day, { day: 'numeric', month: 'long', ...(withYear && { year: 'numeric' }) });
  const count = events.filter((e) => e.start.startsWith(month)).length;

  return (
    <>
      <div className="gps-calendar space-y-4">
        <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {plural(events.length, 'intervention')} à venir sur cette période
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {first === last ? `le ${dayLabel(first)}` : `du ${dayLabel(first)} au ${dayLabel(last)}`}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {byType.map(([type, n]) => (
              <Badge key={type} tone={actionTone(type)} dot>
                {type} · {n}
              </Badge>
            ))}
          </div>
        </header>

        {/* No overflow-hidden: it would clip the "+N autres" popover mounted inside. */}
        <Card>
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-baseline gap-3" aria-live="polite">
              <h3 className="text-sm font-semibold first-letter:uppercase text-gray-900 dark:text-white">
                {fmt(`${month}-01`, { month: 'long', year: 'numeric' })}
              </h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {count === 0 ? 'aucune intervention' : plural(count, 'intervention')}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <NavButton
                label="Mois précédent"
                onClick={() => setOpenMonth(months[index - 1])}
                disabled={index === 0}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </NavButton>
              <NavButton
                label="Mois suivant avec des interventions"
                onClick={() => setOpenMonth(months[index + 1])}
                disabled={index >= months.length - 1}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </NavButton>
            </div>
          </div>
          <div ref={gridRef} className="p-2">
            {/* Keyed on day and month: FullCalendar reads initialDate and now only when it mounts. */}
            <FullCalendar
              key={`${today}|${month}`}
              plugins={[dayGridPlugin]}
              initialView="dayGridMonth"
              initialDate={`${month}-01`}
              now={today}
              timeZone="UTC"
              locale="fr"
              firstDay={1}
              headerToolbar={false}
              fixedWeekCount={false}
              showNonCurrentDates={false}
              events={events}
              eventContent={(arg) => <GPSCalendarEventChip event={arg.event} />}
              dayMaxEvents={3}
              moreLinkText={(extra) => `+${plural(extra, 'autre')}`}
              moreLinkHint={(extra) => `Afficher ${plural(extra, 'intervention')} de plus`}
              closeHint="Fermer"
              height="auto"
              eventClick={(info) => {
                // Ctrl/Cmd/Shift-click keeps the browser's own "open in new tab" on the Jira link.
                if (info.event.url && (info.jsEvent.metaKey || info.jsEvent.ctrlKey || info.jsEvent.shiftKey)) return;
                info.jsEvent.preventDefault();
                setSelected(info.event.extendedProps.issue);
              }}
            />
          </div>
        </Card>
      </div>
      {/* Outside the spaced container: space-y would push the full-screen backdrop down 1rem. */}
      {selected && <GPSCalendarEventModal issue={selected} onClose={() => setSelected(null)} />}
    </>
  );
};

export default GPSCalendar;
